import { useState } from 'react';
import {
  IconBuilding,
  IconMail,
  IconMapPin,
  IconMessage,
  IconPhone,
  IconX,
} from './icons';
import { contactMailLink, contactTelLink, contactWhatsappLink } from './contactLinks';

export type ContactMessageTarget = {
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  company?: string;
  location?: string;
};

type Props = {
  contact: ContactMessageTarget;
  title?: string;
  subtitle?: string;
  contextTitle?: string;
  contextHighlight?: string;
  contextBody?: string;
  defaultReply?: string;
  onClose: () => void;
};

function displayName(c: ContactMessageTarget) {
  const n = `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim();
  return n || 'Client';
}

export function ContactMessageDialog({
  contact,
  title = 'Contacter le client',
  subtitle = 'Coordonnées récupérées depuis la fiche client',
  contextTitle,
  contextHighlight,
  contextBody,
  defaultReply,
  onClose,
}: Props) {
  const first = contact.first_name?.trim() || 'Client';
  const [reply, setReply] = useState(
    defaultReply ??
      `Bonjour ${first},\n\nMerci pour votre confiance. Je vous contacte afin de...\n\nCordialement`,
  );

  const name = displayName(contact);
  const email = contact.email?.trim() || '';
  const phone = contact.phone?.trim() || '';
  const location = contact.location?.trim() || '';
  const company = contact.company?.trim() || '';

  const mailLink = contactMailLink(email, 'Message ISBD', reply);
  const waLink = contactWhatsappLink(phone, reply);
  const telLink = contactTelLink(phone);

  return (
    <div className="crm-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="crm-modal crm-modal--sheet"
        role="dialog"
        aria-labelledby="crm-contact-msg-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="crm-modal-sticky">
          <div className="crm-msg-title-row">
            <div className="crm-msg-icon">
              <IconMessage size={20} />
            </div>
            <div>
              <h2 id="crm-contact-msg-title">{title}</h2>
              <p className="crm-modal-sub">{subtitle}</p>
            </div>
          </div>
          <button type="button" className="crm-modal-x" onClick={onClose} aria-label="Fermer">
            <IconX />
          </button>
        </div>

        <div className="crm-modal-body">
          <div className="crm-msg-card">
            <p className="crm-msg-name">{name}</p>
            {email ? (
              <p className="crm-msg-line">
                <IconMail size={16} /> {email}
              </p>
            ) : (
              <p className="crm-msg-line crm-msg-line--muted">Aucun e-mail enregistré</p>
            )}
            {phone ? (
              <p className="crm-msg-line">
                <IconPhone size={16} /> {phone}
              </p>
            ) : (
              <p className="crm-msg-line crm-msg-line--muted">Aucun téléphone enregistré</p>
            )}
            {location ? (
              <p className="crm-msg-line">
                <IconMapPin size={16} /> {location}
              </p>
            ) : null}
            {company ? (
              <p className="crm-msg-line">
                <IconBuilding size={16} /> {company}
              </p>
            ) : null}
          </div>

          {contextTitle || contextHighlight || contextBody ? (
            <div>
              {contextTitle ? <p className="crm-msg-label">{contextTitle}</p> : null}
              <div className="crm-msg-need">
                {contextHighlight ? <p className="crm-msg-need-title">{contextHighlight}</p> : null}
                {contextBody ? <p className="crm-msg-need-body">{contextBody}</p> : null}
              </div>
            </div>
          ) : null}

          <div>
            <p className="crm-msg-label">Votre message</p>
            <textarea rows={4} value={reply} onChange={(e) => setReply(e.target.value)} />
          </div>

          <div className="crm-msg-actions">
            <a
              href={mailLink || undefined}
              className={`crm-msg-action crm-msg-action--mail${!email ? ' is-disabled' : ''}`}
              aria-disabled={!email}
              onClick={(e) => {
                if (!email) e.preventDefault();
              }}
            >
              <IconMail size={20} />
              <span>Email</span>
            </a>
            <a
              href={waLink || undefined}
              target="_blank"
              rel="noopener noreferrer"
              className={`crm-msg-action crm-msg-action--wa${!phone ? ' is-disabled' : ''}`}
              aria-disabled={!phone}
              onClick={(e) => {
                if (!phone) e.preventDefault();
              }}
            >
              <IconMessage size={20} />
              <span>WhatsApp</span>
            </a>
            <a
              href={telLink || undefined}
              className={`crm-msg-action crm-msg-action--tel${!phone ? ' is-disabled' : ''}`}
              aria-disabled={!phone}
              onClick={(e) => {
                if (!phone) e.preventDefault();
              }}
            >
              <IconPhone size={20} />
              <span>Appeler</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
